import { kundaliRegionalLocale } from "@/i18n/regional-locales";
import type { KundaliLocale } from "./KundaliFormCard.i18n";
import {
  REGIONAL_KUNDALI_FORM_COPY,
  type RegionalValidationKey,
} from "./KundaliFormCard.i18n.regional";
import { isValidOptionalEmail, normalizePhoneE164 } from "@/lib/phone-e164";
import { hasDevanagari } from "@/lib/place-devanagari";

export type KundaliFieldErrors = Partial<
  Record<"name" | "birthDate" | "birthTime" | "birthPlace" | "phone" | "email", string>
>;

type ValidationInput = {
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  /** Display locale: the messages are the only thing it changes. */
  language: KundaliLocale;
  name: string;
  phone: string;
  email: string;
  placeSelected: boolean;
  /** The gazetteer found nothing for the typed town (not ambiguous, not blank). */
  placeNoMatch?: boolean;
  requireBirthTime: boolean;
};

type ValidationResult =
  | { errors: KundaliFieldErrors; ok: false; firstField: keyof KundaliFieldErrors }
  | { birthDateIso: string; errors: KundaliFieldErrors; ok: true };

/**
 * Whether `phone` is a valid mobile number. The phone field emits E.164
 * (`<PhoneInputE164>`), so validity is "a plausible mobile for the picked
 * country" — India (10 digits, 6-9) plus diaspora numbers.
 */
export function isContactGateSatisfied(phone: string): boolean {
  return normalizePhoneE164(phone) !== null;
}

/**
 * Phone is optional (owner decision 2026-09-24): blank is fine, but a number
 * the seeker did type must be valid so we never save a broken contact.
 */
export function isOptionalPhoneValid(phone: string): boolean {
  return !phone.trim() || isContactGateSatisfied(phone);
}

export function normalizeBirthDateInput(input: string): string | null {
  const raw = normalizeBirthDateDigits(input);
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) return validIsoDate(isoMatch[1], isoMatch[2], isoMatch[3]);

  const displayMatch = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw);
  if (displayMatch) return validIsoDate(displayMatch[3], displayMatch[2], displayMatch[1]);

  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  if (compactMatch) return validIsoDate(compactMatch[3], compactMatch[2], compactMatch[1]);

  return null;
}

/**
 * Inline (as-typed) birth-date validity for the field's own feedback — runs on
 * every keystroke, NOT just at submit. Returns a localized error ONLY once the
 * user has typed a COMPLETE date (dd/mm/yyyy, yyyy-mm-dd or eight compact digits),
 * so we never nag mid-typing. This cuts the 8,684 birthDate failures by telling
 * the seeker the date is impossible (a real calendar date but pre-1900 or in the
 * future, or a non-date like 31/02) the moment they finish typing it, instead of
 * only after they tap submit and lose their place in the form.
 *
 * Returns null while the input is incomplete or valid.
 */
export function birthDateInlineError(input: string, language: KundaliLocale): string | null {
  const raw = normalizeBirthDateDigits(input);
  // Only judge a fully-typed date. A display value is complete at dd/mm/yyyy
  // or ISO yyyy-mm-dd, or eight compact digits; shorter input is still being typed.
  const isComplete =
    /^\d{1,2}[./-]\d{1,2}[./-]\d{4}$/.test(raw) ||
    /^\d{4}-\d{2}-\d{2}$/.test(raw) ||
    /^\d{8}$/.test(raw);
  if (!isComplete) return null;

  const iso = normalizeBirthDateInput(raw);
  if (!iso) {
    return localized(
      language,
      "dateInvalid",
      "That date isn't valid — check the day and month.",
      "यह तिथि सही नहीं है — दिन और महीना जाँचें।"
    );
  }
  if (iso < BIRTH_DATE_MIN) {
    return localized(
      language,
      "dateTooEarly",
      "Birth year must be 1900 or later.",
      "जन्म वर्ष 1900 या उसके बाद का होना चाहिए।"
    );
  }
  if (iso > todayIso()) {
    return localized(
      language,
      "dateFuture",
      "Birth date can't be in the future.",
      "जन्म तिथि भविष्य की नहीं हो सकती।"
    );
  }
  return null;
}

export function formatBirthDateDisplayInput(input: string): string {
  const raw = normalizeBirthDateDigits(input);
  const isoDisplay = formatIsoBirthDateInput(raw);
  if (isoDisplay) return isoDisplay;

  const separatedDisplay = formatSeparatedBirthDateInput(raw);
  if (separatedDisplay !== null) return separatedDisplay;

  return formatCompactBirthDateDigits(raw);
}

function normalizeBirthDateDigits(input: string): string {
  // Hindi keyboards and pasted dates may contain Devanagari numerals. Convert
  // before formatting strips non-ASCII characters or validation checks ranges.
  return input.trim().replace(/[०-९]/g, (digit) => String(digit.charCodeAt(0) - 0x0966));
}

function formatIsoBirthDateInput(raw: string): string | null {
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (isoMatch) {
    return [isoMatch[3].padStart(2, "0"), isoMatch[2].padStart(2, "0"), isoMatch[1]].join("/");
  }
  return null;
}

function formatSeparatedBirthDateInput(raw: string): string | null {
  const slashValue = raw.replace(/[.-]/g, "/");
  if (!slashValue.includes("/")) return null;
  const parts = slashValue.split("/");
  const partialDisplayDate =
    parts.length <= 3 &&
    parts.every((part) => /^\d*$/.test(part)) &&
    parts[0].length <= 2 &&
    (parts[1] ?? "").length <= 2 &&
    (parts[2] ?? "").length <= 4;
  return partialDisplayDate ? parts.join("/") : null;
}

function formatCompactBirthDateDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function validateKundaliForm(input: ValidationInput): ValidationResult {
  const errors: KundaliFieldErrors = {};
  if (!input.name.trim()) {
    errors.name = localized(
      input.language,
      "nameRequired",
      "Please enter your name.",
      "कृपया अपना नाम लिखें।"
    );
  }

  const birthDateIso = normalizeBirthDateInput(input.birthDate);
  if (hasInvalidBirthDate(birthDateIso)) {
    errors.birthDate = localized(
      input.language,
      "dateFormat",
      "Enter birth date as dd/mm/yyyy.",
      "जन्म तिथि dd/mm/yyyy रूप में लिखें।"
    );
  }

  if (isMissingRequiredBirthTime(input)) {
    errors.birthTime = localized(
      input.language,
      "timeRequired",
      "Enter birth time to start the chat.",
      "चैट के लिए जन्म समय ज़रूरी है।"
    );
  }

  // Blank vs unresolved are DIFFERENT failures with different corrections: a
  // blank field needs typing; an unresolved one (no gazetteer match, or an
  // ambiguous city whose did-you-mean chip is showing) needs a spelling check
  // or a suggestion tap. Typed cities now resolve on submit (incl. Devanagari),
  // so the old "pick from the dropdown" instruction was misleading.
  const placeError = birthPlaceError(input);
  if (placeError) errors.birthPlace = placeError;

  if (hasInvalidPhone(input)) {
    errors.phone = localized(
      input.language,
      "phoneInvalid",
      "Enter a valid mobile number for the country you picked, or leave it blank.",
      "चुने गए देश का सही मोबाइल नंबर डालें, या खाली छोड़ दें।"
    );
  }

  if (input.email.trim() && !isValidOptionalEmail(input.email)) {
    errors.email = localized(
      input.language,
      "emailInvalid",
      "Enter a valid email, or leave it blank.",
      "सही ईमेल डालें, या खाली छोड़ दें।"
    );
  }

  const firstField = firstErrorField(errors);
  if (firstField) return { errors, firstField, ok: false };
  return { birthDateIso: birthDateIso as string, errors, ok: true };
}

/**
 * Blank, not found, and ambiguous are different corrections. "Tap a
 * suggestion" only fits the ambiguous case, where a did-you-mean chip exists;
 * a town the gazetteer cannot find gets a real way forward instead (C20).
 */
function birthPlaceError(input: ValidationInput): string | null {
  if (!input.birthPlace.trim()) {
    return localized(
      input.language,
      "placeRequired",
      "Please enter your birth city.",
      "अपना जन्म स्थान लिखें।"
    );
  }
  if (input.placeSelected) return null;
  if (input.placeNoMatch && hasDevanagari(input.birthPlace)) {
    return localized(
      input.language,
      "placeNoMatch",
      "We couldn't match that Hindi spelling — type the town in English, e.g. Sitapur.",
      "हिंदी में लिखा यह नाम सूची में नहीं मिला — शहर का नाम अंग्रेज़ी में लिखें (जैसे Sitapur)।"
    );
  }
  if (input.placeNoMatch) {
    return localized(
      input.language,
      "placeNoMatch",
      "We couldn't find that town in our list — try your district or the nearest bigger town, in English spelling.",
      "यह नाम हमारी सूची में नहीं मिला — अपना ज़िला या पास का बड़ा शहर अंग्रेज़ी में लिखें (जैसे Lucknow)।"
    );
  }
  return localized(
    input.language,
    "placeUnconfirmed",
    "We couldn't confirm that city — check the spelling or tap a suggestion.",
    "यह शहर पक्का नहीं हो पाया — वर्तनी जाँचें या कोई सुझाव चुनें।"
  );
}

function firstErrorField(errors: KundaliFieldErrors): keyof KundaliFieldErrors | null {
  if (errors.name) return "name";
  if (errors.birthDate) return "birthDate";
  if (errors.birthTime) return "birthTime";
  if (errors.birthPlace) return "birthPlace";
  // Phone (optional) is the last field in the shared form. Error focus follows
  // the visible journey instead of jumping from name straight to contact.
  if (errors.phone) return "phone";
  if (errors.email) return "email";
  return null;
}

function hasInvalidPhone(input: ValidationInput): boolean {
  return !isOptionalPhoneValid(input.phone);
}

function localized(language: KundaliLocale, key: RegionalValidationKey, en: string, hi: string) {
  const regional = kundaliRegionalLocale(language);
  if (regional) return REGIONAL_KUNDALI_FORM_COPY[regional].validation[key];
  return language === "hi" ? hi : en;
}

function hasInvalidBirthDate(birthDateIso: string | null) {
  return !birthDateIso || !isAllowedBirthDate(birthDateIso);
}

function isMissingRequiredBirthTime(input: ValidationInput) {
  return input.requireBirthTime && !input.birthTime.trim();
}

function validIsoDate(year: string, month: string, day: string): string | null {
  const yyyy = Number(year);
  const mm = Number(month);
  const dd = Number(day);
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (date.getUTCFullYear() !== yyyy || date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) {
    return null;
  }
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

// Single source of truth for the allowed birth-date window, shared by the
// submit-time validator and the inline as-typed field check so they can't drift.
const BIRTH_DATE_MIN = "1900-01-01";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isAllowedBirthDate(isoDate: string) {
  return isoDate >= BIRTH_DATE_MIN && isoDate <= todayIso();
}
